import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  Briefcase, Megaphone, HeadphonesIcon, BarChart3,
  Rocket, CalendarCheck, Settings, MessageCircle,
  ArrowRight, Sparkles, MessageSquareText, Cpu,
  Layers, Target, Clock, Puzzle, Zap, CheckCircle2,
  ChevronRight, Home, Plug, Workflow, Globe,
  BadgeDollarSign, Calendar, Mail, CreditCard,
  FileText, Database, MessageSquare, Brain
} from 'lucide-react';

const LOGO_URL = "/images/area-logo.png";

const managesItems = [
  { icon: Briefcase, title: 'Sales & Lead Generation', description: 'Finds leads, creates outreach, and helps move deals forward.' },
  { icon: Megaphone, title: 'Marketing & Content', description: 'Builds campaigns, writes content, and supports promotion.' },
  { icon: HeadphonesIcon, title: 'Customer Support', description: 'Handles customer conversations with speed, care, and consistency.' },
  { icon: BarChart3, title: 'Data & Analytics', description: 'Turns business data into clear insights and better decisions.' },
  { icon: Rocket, title: 'Strategy & Growth', description: 'Helps identify opportunities and plan the next move.' },
  { icon: CalendarCheck, title: 'Executive Assistant', description: 'Keeps communication, scheduling, and daily workflow organized.' },
  { icon: Settings, title: 'Operations & Admin', description: 'Supports the systems and follow-through that keep business moving.' },
  { icon: MessageCircle, title: 'Social Media', description: 'Creates and organizes social content that drives engagement and growth.' },
];

const howItWorks = [
  { step: '01', title: 'You Ask', description: 'Just tell Moxey what you need in plain language.', icon: MessageSquareText },
  { step: '02', title: 'Moxey Coordinates', description: 'Moxey decides which native modules or connected tools to use, then orchestrates them behind the scenes.', icon: Cpu },
  { step: '03', title: 'You Get Results', description: 'Instead of juggling tools and tasks, you get clear output, next steps, and work completed faster.', icon: Target },
];

const differentiators = [
  { icon: Layers, title: 'One AI, many capabilities', description: "You don't need to remember different tools or workflows. Just talk to Moxey." },
  { icon: Plug, title: 'Native modules and third-party tools', description: 'Moxey works across its own modules and your existing business software from one place.' },
  { icon: Brain, title: 'One shared memory and orchestration layer', description: 'Moxey remembers context across every interaction and coordinates systems with a unified intelligence.' },
  { icon: Target, title: 'Delivers results, not just responses', description: 'Moxey is designed to get work done across your entire business, not just chat.' },
];

const examplePrompts = [
  'Get me more leads this month',
  'Follow up with all active prospects',
  'Run a commission report',
  'Build a marketing campaign',
  'Summarize my pipeline',
  'Help me organize my day',
];

const benefits = [
  { icon: Clock, title: 'Save time', description: 'Reduce hours spent on repetitive coordination and manual tasks.' },
  { icon: Puzzle, title: 'Reduce complexity', description: 'One AI replaces the need to juggle multiple tools and logins.' },
  { icon: CheckCircle2, title: 'Improve follow-through', description: 'Nothing falls through the cracks when Moxey is on it.' },
  { icon: Zap, title: 'Get more done', description: 'Move faster with AI handling the heavy lifting across departments.' },
  { icon: Settings, title: 'Keep work organized', description: 'Stay on top of tasks, contacts, and deadlines without the chaos.' },
  { icon: Rocket, title: 'Make faster decisions', description: 'Get insights and summaries instantly instead of digging through data.' },
];

function SectionLabel({ children }) {
  return (
    <p className="text-[#2563eb] font-semibold text-sm tracking-wide uppercase font-body mb-3">{children}</p>
  );
}

export default function MoxeyAI() {
  return (
    <div className="min-h-screen bg-[#f8f9fb]" data-testid="moxey-ai-page">

      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-lg border-b border-gray-100/80" data-testid="moxey-ai-nav">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="moxey-nav-logo">
            <img src={LOGO_URL} alt="AREA" className="h-9 w-auto" />
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/login">
              <Button variant="ghost" className="text-sm font-medium text-gray-600 hover:text-gray-900 rounded-full px-5" data-testid="moxey-nav-login">
                Sign In
              </Button>
            </Link>
            <Link to="/app">
              <Button className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white rounded-full px-6 text-sm font-semibold shadow-sm" data-testid="moxey-nav-app">
                Open App
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* ─── HERO ─── */}
      <section className="pt-16 pb-20 lg:pt-24 lg:pb-28 px-6" data-testid="moxey-hero-section">
        <div className="max-w-[1000px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-16 items-center">

            {/* Left */}
            <div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-gray-900 tracking-tight leading-[1.1]" data-testid="moxey-hero-title">
                <span className="font-script text-[#2563eb] font-bold">Meet</span>{' '}Moxey
              </h1>
              <p className="text-[#2563eb] mt-3 text-lg sm:text-xl font-semibold font-body" data-testid="moxey-hero-subtitle">
                Your AI Business Leader
              </p>
              <p className="text-gray-500 mt-5 text-base sm:text-lg leading-relaxed font-body max-w-md" data-testid="moxey-hero-copy">
                Moxey works across your business — not just inside one app. It coordinates native modules and third-party tools through one intelligent orchestration layer.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 mt-8" data-testid="moxey-hero-cta">
                <Link to="/hire-moxey">
                  <Button className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white rounded-2xl px-8 py-6 text-base font-bold shadow-lg shadow-blue-500/20 hover:shadow-xl hover:shadow-blue-500/30 transition-all" data-testid="moxey-hire-btn">
                    Hire Moxey
                    <img src="/images/moxey-avatar.png" alt="Moxey" className="ml-2 w-5 h-5 rounded-full object-cover" />
                  </Button>
                </Link>
                <a href="#how-it-works">
                  <Button variant="outline" className="rounded-2xl px-8 py-6 text-base font-semibold text-gray-700 border-gray-200 hover:bg-gray-50 transition-colors" data-testid="moxey-how-works-btn">
                    See How Moxey Works
                    <ChevronRight className="ml-1 w-4 h-4" />
                  </Button>
                </a>
              </div>
            </div>

            {/* Right – Image */}
            <div className="flex justify-center" data-testid="moxey-hero-image-container">
              <div className="bg-white rounded-3xl shadow-[0_4px_32px_rgba(0,0,0,0.06),0_1px_4px_rgba(0,0,0,0.04)] border border-gray-100/60 p-4 sm:p-6">
                <img
                  src="/images/moxey-meet.png"
                  alt="Moxey AI Business Leader"
                  className="rounded-2xl object-contain max-h-[440px] w-full"
                  data-testid="moxey-hero-image"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── SECTION 1 – What Moxey Manages ─── */}
      <section className="py-20 lg:py-28 px-6 bg-white" data-testid="moxey-manages-section">
        <div className="max-w-[1000px] mx-auto">
          <div className="text-center mb-14">
            <SectionLabel>Capabilities</SectionLabel>
            <h2 className="text-3xl sm:text-4xl font-semibold text-gray-900 tracking-tight" data-testid="moxey-manages-title">
              What Moxey Manages
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="moxey-manages-grid">
            {managesItems.map((item, i) => (
              <div
                key={i}
                className="bg-[#f8f9fb] rounded-2xl p-6 border border-gray-100/80 hover:shadow-[0_4px_24px_rgba(37,99,235,0.07)] transition-shadow duration-300 group"
                data-testid={`moxey-manages-${i}`}
              >
                <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center mb-4 group-hover:bg-[#2563eb] transition-colors duration-300">
                  <item.icon className="w-5 h-5 text-[#2563eb] group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="font-bold text-gray-900 text-[15px] mb-1.5 font-body">{item.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed font-body">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── SECTION 2 – How Moxey Works ─── */}
      <section id="how-it-works" className="py-20 lg:py-28 px-6" data-testid="moxey-how-section">
        <div className="max-w-[900px] mx-auto">
          <div className="text-center mb-14">
            <SectionLabel>Process</SectionLabel>
            <h2 className="text-3xl sm:text-4xl font-semibold text-gray-900 tracking-tight" data-testid="moxey-how-title">
              How Moxey Works
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="moxey-how-grid">
            {howItWorks.map((step, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl p-8 border border-gray-100/80 shadow-[0_1px_3px_rgba(0,0,0,0.04),0_4px_16px_rgba(0,0,0,0.03)] text-center relative"
                data-testid={`moxey-step-${i}`}
              >
                <span className="text-[#2563eb]/10 font-bold text-6xl absolute top-4 right-6 font-body select-none">{step.step}</span>
                <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto mb-5">
                  <step.icon className="w-7 h-7 text-[#2563eb]" />
                </div>
                <h3 className="font-bold text-gray-900 text-lg mb-2 font-body">{step.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed font-body">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── SECTION 3 – What Makes Moxey Different ─── */}
      <section className="py-20 lg:py-28 px-6 bg-white" data-testid="moxey-different-section">
        <div className="max-w-[1000px] mx-auto">
          <div className="text-center mb-14">
            <SectionLabel>Why Moxey</SectionLabel>
            <h2 className="text-3xl sm:text-4xl font-semibold text-gray-900 tracking-tight" data-testid="moxey-different-title">
              What Makes Moxey Different
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-testid="moxey-different-grid">
            {differentiators.map((item, i) => (
              <div
                key={i}
                className="bg-[#f8f9fb] rounded-2xl p-8 border border-gray-100/80 hover:shadow-[0_4px_24px_rgba(37,99,235,0.07)] transition-shadow duration-300 group"
                data-testid={`moxey-diff-${i}`}
              >
                <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center mb-5 group-hover:bg-[#2563eb] transition-colors duration-300">
                  <item.icon className="w-5 h-5 text-[#2563eb] group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="font-bold text-gray-900 text-base mb-2 font-body">{item.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed font-body">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── NEW: Architecture & Integrations ─── */}
      <section className="py-20 lg:py-28 px-6" data-testid="moxey-integrations-section">
        <div className="max-w-[1000px] mx-auto">
          <div className="text-center mb-14">
            <SectionLabel>Architecture</SectionLabel>
            <h2 className="text-3xl sm:text-4xl font-semibold text-gray-900 tracking-tight" data-testid="moxey-integrations-title">
              One assistant. One brain. Many tools.
            </h2>
            <p className="text-gray-500 mt-3 text-base max-w-lg mx-auto font-body leading-relaxed">
              Moxey connects to native modules and third-party business software through a modular integration layer.
            </p>
          </div>

          {/* Architecture Visual */}
          <div className="bg-white rounded-2xl border border-gray-100/80 shadow-[0_1px_3px_rgba(0,0,0,0.04),0_4px_16px_rgba(0,0,0,0.03)] p-8 mb-8" data-testid="moxey-architecture">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
              <div className="p-5 rounded-xl bg-[#f8f9fb]">
                <Sparkles className="w-6 h-6 text-[#2563eb] mx-auto mb-3" />
                <p className="font-bold text-gray-900 text-sm font-body">One Visible Assistant</p>
                <p className="text-gray-500 text-xs font-body mt-1">Users just talk to Moxey</p>
              </div>
              <div className="p-5 rounded-xl bg-[#f8f9fb]">
                <Brain className="w-6 h-6 text-[#2563eb] mx-auto mb-3" />
                <p className="font-bold text-gray-900 text-sm font-body">Shared Memory Layer</p>
                <p className="text-gray-500 text-xs font-body mt-1">Context persists across every action</p>
              </div>
              <div className="p-5 rounded-xl bg-[#f8f9fb]">
                <Cpu className="w-6 h-6 text-[#2563eb] mx-auto mb-3" />
                <p className="font-bold text-gray-900 text-sm font-body">Orchestration Layer</p>
                <p className="text-gray-500 text-xs font-body mt-1">Routes to the right module or tool</p>
              </div>
            </div>
          </div>

          {/* Native Modules */}
          <p className="text-xs font-bold text-gray-400 uppercase tracking-wider font-body mb-4">Native Modules</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            {[
              { icon: Workflow, name: 'Moxey Flow', sub: 'CRM, Commissions, E-Sign', color: 'text-violet-600 bg-violet-50', border: 'border-violet-100' },
            ].map((mod, i) => (
              <div key={i} className={`bg-white rounded-2xl p-5 border ${mod.border} flex items-center gap-3`} data-testid={`moxey-native-${i}`}>
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${mod.color}`}>
                  <mod.icon className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-bold text-gray-900 text-sm font-body">{mod.name}</p>
                  <p className="text-gray-500 text-xs font-body">{mod.sub}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Third-Party Integrations */}
          <p className="text-xs font-bold text-gray-400 uppercase tracking-wider font-body mb-4">Third-Party Integrations</p>
          <div className="bg-white rounded-2xl border border-gray-100/80 shadow-[0_1px_3px_rgba(0,0,0,0.04)] p-6" data-testid="moxey-third-party">
            <div className="flex flex-wrap gap-3 justify-center sm:justify-start">
              {[
                { icon: Calendar, label: 'Google Calendar' },
                { icon: Mail, label: 'Gmail / Email' },
                { icon: MessageSquare, label: 'Slack' },
                { icon: CreditCard, label: 'QuickBooks' },
                { icon: FileText, label: 'Google Docs' },
                { icon: Database, label: 'External CRM' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#f8f9fb] border border-gray-100/80" data-testid={`moxey-integration-${i}`}>
                  <item.icon className="w-4 h-4 text-gray-400" />
                  <span className="text-sm font-medium text-gray-600 font-body">{item.label}</span>
                </div>
              ))}
            </div>
            <p className="text-gray-400 text-xs font-body mt-4 flex items-center gap-1.5">
              <Plug className="w-3 h-3" />
              Moxey connects to more tools through its modular integration layer.
            </p>
          </div>
        </div>
      </section>

      {/* ─── SECTION 4 – Example Requests ─── */}
      <section className="py-20 lg:py-28 px-6" data-testid="moxey-examples-section">
        <div className="max-w-[900px] mx-auto">
          <div className="text-center mb-14">
            <SectionLabel>Examples</SectionLabel>
            <h2 className="text-3xl sm:text-4xl font-semibold text-gray-900 tracking-tight" data-testid="moxey-examples-title">
              Try asking Moxey things like:
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="moxey-examples-grid">
            {examplePrompts.map((prompt, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl px-6 py-5 border border-gray-100/80 shadow-[0_1px_3px_rgba(0,0,0,0.04),0_4px_16px_rgba(0,0,0,0.03)] flex items-center gap-3 hover:border-[#2563eb]/30 transition-colors duration-300"
                data-testid={`moxey-example-${i}`}
              >
                <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                  <MessageSquareText className="w-4 h-4 text-[#2563eb]" />
                </div>
                <p className="text-gray-700 text-sm font-medium font-body leading-snug">{prompt}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── SECTION 5 – Why Businesses Use Moxey ─── */}
      <section className="py-20 lg:py-28 px-6 bg-white" data-testid="moxey-benefits-section">
        <div className="max-w-[1000px] mx-auto">
          <div className="text-center mb-14">
            <SectionLabel>Benefits</SectionLabel>
            <h2 className="text-3xl sm:text-4xl font-semibold text-gray-900 tracking-tight" data-testid="moxey-benefits-title">
              Why businesses use Moxey
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="moxey-benefits-grid">
            {benefits.map((b, i) => (
              <div
                key={i}
                className="bg-[#f8f9fb] rounded-2xl p-7 border border-gray-100/80 hover:shadow-[0_4px_24px_rgba(37,99,235,0.07)] transition-shadow duration-300 group"
                data-testid={`moxey-benefit-${i}`}
              >
                <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center mb-4 group-hover:bg-[#2563eb] transition-colors duration-300">
                  <b.icon className="w-5 h-5 text-[#2563eb] group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="font-bold text-gray-900 text-[15px] mb-1.5 font-body">{b.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed font-body">{b.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── SECTION 6 – Final CTA ─── */}
      <section className="py-20 lg:py-28 px-6" data-testid="moxey-final-cta-section">
        <div className="max-w-[640px] mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-semibold text-gray-900 tracking-tight mb-4" data-testid="moxey-final-cta-title">
            Put Moxey to work
          </h2>
          <p className="text-gray-500 text-base sm:text-lg leading-relaxed font-body mb-10 max-w-md mx-auto">
            Bring sales, marketing, operations, and execution together — across native tools and connected systems — with one AI business leader.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-testid="moxey-final-cta-buttons">
            <Link to="/hire-moxey">
              <Button className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white rounded-2xl px-10 py-6 text-base font-bold shadow-lg shadow-blue-500/20 hover:shadow-xl hover:shadow-blue-500/30 transition-all" data-testid="moxey-final-hire-btn">
                Hire Moxey
                <img src="/images/moxey-avatar.png" alt="Moxey" className="ml-2 w-5 h-5 rounded-full object-cover" />
              </Button>
            </Link>
            <Link to="/">
              <Button variant="outline" className="rounded-2xl px-10 py-6 text-base font-semibold text-gray-700 border-gray-200 hover:bg-gray-50 transition-colors" data-testid="moxey-final-home-btn">
                <Home className="mr-2 w-4 h-4" />
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-100 bg-white py-8 px-6">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="AREA" className="h-7 w-auto opacity-60" />
            <span className="text-gray-400 text-xs font-body">American Real Estate Alliance</span>
          </div>
          <p className="text-gray-400 text-xs font-body">&copy; {new Date().getFullYear()} AREA. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
